/**
 * Synchronous RLE mask decoder for SAM3 predictions
 * Decodes COCO RLE format to RGBA buffers for canvas rendering
 */

import Annotation from "@roboflow/annotation";

interface MaskData {
    counts: string;
    size: [number, number];
}

interface Bounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    width: number;
    height: number;
}

/**
 * Decode RLE to binary mask
 */
function rleToBinaryMask(rleString: string, width: number, height: number): Uint8Array {
    const mask = Annotation.rleToBinaryMask(rleString, width, height, { flat: true });
    return new Uint8Array(mask);
}

/**
 * Detect border pixels
 */
function detectBorders(
    mask: Uint8Array,
    width: number,
    height: number,
    borderThickness: number
): Uint8Array {
    const borderMap = new Uint8Array(width * height);

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const idx = y * width + x;
            if (!mask[idx]) continue;

            if (
                x < borderThickness ||
                x >= width - borderThickness ||
                y < borderThickness ||
                y >= height - borderThickness
            ) {
                borderMap[idx] = 1;
                continue;
            }

            let isBorder = false;
            for (let d = 1; d <= borderThickness; d++) {
                const upIdx = (y - d) * width + x;
                const downIdx = (y + d) * width + x;
                const leftIdx = idx - d;
                const rightIdx = idx + d;

                if (!mask[upIdx] || !mask[downIdx] || !mask[leftIdx] || !mask[rightIdx]) {
                    isBorder = true;
                    break;
                }
            }

            borderMap[idx] = isBorder ? 1 : 0;
        }
    }

    return borderMap;
}

/**
 * Calculate bounding box
 */
function calculateMaskBounds(mask: Uint8Array, maskW: number, maskH: number): Bounds | null {
    let minX = maskW;
    let minY = maskH;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < maskH; y++) {
        for (let x = 0; x < maskW; x++) {
            if (mask[y * maskW + x]) {
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
            }
        }
    }

    if (minX >= maskW || minY >= maskH) {
        return null;
    }

    return { minX, minY, maxX, maxY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

/**
 * Create RGBA buffer
 */
function createRGBABuffer(
    binaryMask: Uint8Array,
    width: number,
    height: number,
    bounds: Bounds | null,
    showOutline: boolean
): { rgbaData: Uint8Array; roiWidth: number; roiHeight: number; offsetX: number; offsetY: number } {
    const r = 255;
    const g = 255;
    const b = 255;

    let borderMap: Uint8Array | null = null;
    if (showOutline) {
        const borderThickness = Math.max(1, Math.floor(Math.min(width, height) / 250));
        borderMap = detectBorders(binaryMask, width, height, borderThickness);
    }

    const useROI = bounds && bounds.width < width && bounds.height < height;

    let rgbaData: Uint8Array;
    let roiWidth: number;
    let roiHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (useROI && bounds) {
        roiWidth = bounds.width;
        roiHeight = bounds.height;
        offsetX = bounds.minX;
        offsetY = bounds.minY;
        rgbaData = new Uint8Array(roiWidth * roiHeight * 4);

        for (let y = 0; y < roiHeight; y++) {
            for (let x = 0; x < roiWidth; x++) {
                const maskY = offsetY + y;
                const maskX = offsetX + x;
                const maskIdx = maskY * width + maskX;
                const rgbaIdx = (y * roiWidth + x) * 4;

                if (binaryMask[maskIdx]) {
                    rgbaData[rgbaIdx] = r;
                    rgbaData[rgbaIdx + 1] = g;
                    rgbaData[rgbaIdx + 2] = b;
                    rgbaData[rgbaIdx + 3] = borderMap && borderMap[maskIdx] ? 230 : 120;
                }
            }
        }
    } else {
        roiWidth = width;
        roiHeight = height;
        offsetX = 0;
        offsetY = 0;
        rgbaData = new Uint8Array(width * height * 4);

        for (let i = 0; i < binaryMask.length; i++) {
            if (binaryMask[i]) {
                const rgbaIdx = i * 4;
                rgbaData[rgbaIdx] = r;
                rgbaData[rgbaIdx + 1] = g;
                rgbaData[rgbaIdx + 2] = b;
                rgbaData[rgbaIdx + 3] = borderMap && borderMap[i] ? 230 : 120;
            }
        }
    }

    return { rgbaData, roiWidth, roiHeight, offsetX, offsetY };
}

/**
 * Decode RLE mask synchronously (main thread)
 * @param masks - RLE mask data
 * @param showOutline - Whether to show border outline (default: false)
 */
export function decodeMaskSync(
    masks: MaskData,
    showOutline: boolean = false
): { imageData: ImageData; bounds: Bounds | null; offsetX: number; offsetY: number } {
    const maskWidth = masks.size[1];
    const maskHeight = masks.size[0];

    const binaryMask = rleToBinaryMask(masks.counts, maskWidth, maskHeight);
    const maskBounds = calculateMaskBounds(binaryMask, maskWidth, maskHeight);
    const rgba = createRGBABuffer(binaryMask, maskWidth, maskHeight, maskBounds, showOutline);

    const imageData = new ImageData(
        new Uint8ClampedArray(rgba.rgbaData),
        rgba.roiWidth,
        rgba.roiHeight
    );

    return { imageData, bounds: maskBounds, offsetX: rgba.offsetX, offsetY: rgba.offsetY };
}
